import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Check } from 'lucide-react';

import { i18n } from 'utils/with-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const languages = [
  { value: 'ko', label: 'languages.ko' },
  { value: 'en', label: 'languages.en' },
  { value: 'ja', label: 'languages.ja' },
];

// 저장된 언어 가져오기 (cookie > localStorage > navigator > i18n.language)
const getStoredLanguage = () => {
  if (typeof window === 'undefined') return 'ko';

  // cookie에서 확인
  const cookieMatch = document.cookie.match(/i18next=([^;]+)/);
  if (cookieMatch) {
    return cookieMatch[1];
  }

  // localStorage에서 확인
  const stored = localStorage.getItem('i18nextLng');
  if (stored) {
    return stored;
  }

  // 브라우저 언어에서 확인
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang) {
    return browserLang;
  }

  // i18n에서 확인
  return i18n.language || 'ko';
};

// 언어를 ko 코드로 매핑하는 헬퍼 함수
const mapLanguage = lng => {
  if (!lng) return 'ko';
  if (lng.startsWith('ko')) return 'ko';
  // 지원하는 언어인지 확인
  const supported = languages.find(l => l.value === lng);
  return supported ? lng : 'ko';
};

export function SelectLanguages({ t }) {
  const [select, setSelect] = useState('ko');

  useEffect(() => {
    // 언어 변경 이벤트 핸들러
    const handleLanguageChanged = lng => {
      const mappedLang = mapLanguage(lng);
      setSelect(mappedLang);
    };

    // 초기값 설정 - 저장된 언어에서 직접 읽기
    const storedLang = getStoredLanguage();
    const mappedLang = mapLanguage(storedLang);

    setSelect(mappedLang);

    // 현재 i18n 언어와 다르면 변경
    if (i18n.language !== mappedLang) {
      i18n.changeLanguage(mappedLang);
    }

    // 언어 변경 이벤트 구독
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const handleSelect = value => {
    if (value !== select) {
      setSelect(value); // 즉시 UI 업데이트
      i18n.changeLanguage(value);
    }
  };

  const currentLabel = languages.find(lang => lang.value === select)?.label || 'languages.ko';

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-1.5 text-sm bg-transparent text-inherit outline-none hover:opacity-80">
        {t(currentLabel)}
        <ChevronDown className="h-4 w-4 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[9999]">
        {languages.map(lang => (
          <DropdownMenuItem
            key={lang.value}
            onClick={() => handleSelect(lang.value)}
            className="flex items-center justify-between"
          >
            {t(lang.label)}
            {select === lang.value && <Check className="h-4 w-4 ml-2" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

SelectLanguages.propTypes = {
  t: PropTypes.func,
};

export default SelectLanguages;
